import styled from "styled-components";

import { SWReactCountryIcons } from "@similarweb/icons";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { ChangeCell, ProgressBarWithValueCell, TextCell } from "@similarweb/pro-logic-components";
import { LegendBullet } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import {
    abbrNumberSupportNegativeFilter,
    changeFilter,
    countryTextByIdFilter,
    percentageSignFilter,
} from "app/filters/ngFilters";

import type { ICategoryComparison } from "../../categories-context";
import type { GeographyComparisonTableRow } from "../types";

const formatters = {
    percentageSign: (value: number) => percentageSignFilter()(value, 2),
    // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
    abbrNumber: (value: number) => abbrNumberSupportNegativeFilter()(value, 2),
    changeFilter: (value) => changeFilter()(value),
};

const StyledLegendWrapper = styled.div`
    display: inline-block;
`;

const renderTitle = (category: ICategoryComparison) => (
    <StyledLegendWrapper>
        <LegendBullet
            labelColor={category.color}
            text={<Text fontVariant="captionTitle">{category.category.text}</Text>}
        />
    </StyledLegendWrapper>
);

const renderCountryCell = (countryCode: number) => {
    const countryName = countryTextByIdFilter()(countryCode);
    return (
        <Flex
            alignItems="center"
            gap="8px"
        >
            <SWReactCountryIcons
                size="xs"
                countryCode={countryCode}
            />
            <Text fontVariant="input2">{countryName}</Text>
        </Flex>
    );
};

export const getComparisonTableColumnsConfig = (
    categories: ICategoryComparison[],
): ColumnsType<GeographyComparisonTableRow> => [
    {
        key: "country",
        title: translate("industry.analysis.geo.table.column.country"),
        width: 175,
        resize: false,
        render: (_, row: GeographyComparisonTableRow) => renderCountryCell(row.country),
    },
    {
        key: "primaryCategory",
        title: renderTitle(categories[0]),
        children: [
            {
                key: "primaryCategory.trafficShare",
                title: translate("industry.analysis.geo.table.column.traffic.share"),
                width: 140,
                sorter: (prev, next) =>
                    (prev.primaryCategory?.trafficShare || 0) - (next.primaryCategory?.trafficShare || 0),
                render: (_, row: GeographyComparisonTableRow) => (
                    <ProgressBarWithValueCell
                        value={formatters.percentageSign(row.primaryCategory?.trafficShare)}
                        share={row.primaryCategory?.trafficShare}
                    />
                ),
            },
            {
                key: "primaryCategory.visits",
                title: translate("industry.analysis.geo.table.column.visits"),
                width: 90,
                align: "right",
                sorter: (prev, next) => (prev.primaryCategory?.visits || 0) - (next.primaryCategory?.visits || 0),
                render: (_, row: GeographyComparisonTableRow) => (
                    <TextCell fontVariant="input2">{formatters.abbrNumber(row.primaryCategory?.visits)}</TextCell>
                ),
            },
            {
                key: "primaryCategory.growth",
                title: translate("industry.analysis.geo.table.column.growth"),
                width: 105,
                align: "right",
                sorter: (prev, next) => (prev.primaryCategory?.growth || 0) - (next.primaryCategory?.growth || 0),
                render: (_, row: GeographyComparisonTableRow) => (
                    <TextCell>{formatters.abbrNumber(row.primaryCategory?.growth)}</TextCell>
                ),
            },
            {
                key: "primaryCategory.change",
                title: translate("industry.analysis.geo.table.column.growth.percentage"),
                width: 115,
                align: "right",
                sorter: (prev, next) => (prev.primaryCategory?.change || 0) - (next.primaryCategory?.change || 0),
                render: (_, row: GeographyComparisonTableRow) => (
                    <ChangeCell
                        value={row.primaryCategory?.change}
                        formattedValue={formatters.changeFilter(row.primaryCategory?.change)}
                    />
                ),
            },
        ],
    },
    {
        key: "comparedCategory",
        title: renderTitle(categories[1]),
        children: [
            {
                key: "comparedCategory.trafficShare",
                title: translate("industry.analysis.geo.table.column.traffic.share"),
                width: 140,
                sorter: (prev, next) =>
                    (prev.comparedCategory?.trafficShare || 0) - (next.comparedCategory?.trafficShare || 0),
                render: (_, row: GeographyComparisonTableRow) => (
                    <ProgressBarWithValueCell
                        value={formatters.percentageSign(row.comparedCategory?.trafficShare)}
                        share={row.comparedCategory?.trafficShare}
                    />
                ),
            },
            {
                key: "comparedCategory.visits",
                title: translate("industry.analysis.geo.table.column.visits"),
                width: 90,
                align: "right",
                sorter: (prev, next) => (prev.comparedCategory?.visits || 0) - (next.comparedCategory?.visits || 0),
                render: (_, row: GeographyComparisonTableRow) => (
                    <TextCell>{formatters.abbrNumber(row.comparedCategory?.visits)}</TextCell>
                ),
            },
            {
                key: "comparedCategory.growth",
                title: translate("industry.analysis.geo.table.column.growth"),
                width: 105,
                align: "right",
                sorter: (prev, next) => (prev.comparedCategory?.growth || 0) - (next.comparedCategory?.growth || 0),
                render: (_, row: GeographyComparisonTableRow) => (
                    <TextCell>{formatters.abbrNumber(row.comparedCategory?.growth)}</TextCell>
                ),
            },
            {
                key: "comparedCategory.change",
                title: translate("industry.analysis.geo.table.column.growth.percentage"),
                width: 115,
                align: "right",
                sorter: (prev, next) => (prev.comparedCategory?.change || 0) - (next.comparedCategory?.change || 0),
                render: (_, row: GeographyComparisonTableRow) => (
                    <ChangeCell
                        value={row.comparedCategory?.change}
                        formattedValue={formatters.changeFilter(row.comparedCategory?.change)}
                    />
                ),
            },
        ],
    },
];
